/**
 * @fileoverview Создание и спавн бортов.
 */

import {
  AIRLINES,
  FLIGHT_KINDS,
  TYPES,
  type AircraftTypeKey,
  type FlightKindKey,
} from "../constants/aircraft";
import type { GameHooks, GameState, Plane } from "../state/types";
import { pick, rnd, rndi } from "../util/math";
import { orbitPos } from "./orbit";

/**
 * Собрать объект самолета (еще не на поле).
 */
export function createPlane(
  g: GameState,
  typeKey: AircraftTypeKey,
  priority = false,
): Plane {
  const T = TYPES[typeKey];
  let kind: FlightKindKey = "normal";
  const r = Math.random();
  if (typeKey === "cargo") kind = "cargo";
  else if (priority || r < 0.08) kind = pick(["medevac", "lowfuel"] as const);
  else if (r < 0.18) kind = "vip";
  else if (r < 0.32) kind = "connect";
  else if (r < 0.48) kind = "intl";
  const FK = FLIGHT_KINDS[kind];

  return {
    id: g.idc++,
    cs: `${pick(AIRLINES)} ${rndi(100, 989)}`,
    type: typeKey,
    T,
    kind,
    FK,
    priority: !!priority || FK.prio >= 3,
    passengers: T.pax ? Math.round(T.pax * rnd(0.7, 1.1)) : 0,
    needsDeice: false,
    deiced: false,
    deiceWaive: false,
    deiceT: 0,
    deiceMax: 0,
    x: 0,
    y: 0,
    ang: 0,
    v: 0,
    alt: 1,
    status: "approach",
    path: [],
    rwy: null,
    gate: null,
    apron: null,
    hold: null,
    pat: 1,
    patRate: 1 / (T.pat * (priority || FK.prio >= 3 ? 0.58 : 1)),
    svcLeft: 0,
    svcTotal: T.svc,
    orbit: rnd(0, Math.PI * 2),
    orbitR: 0,
    orbitDir: Math.random() < 0.5 ? 1 : -1,
    waited: 0,
    landT: 0,
    gateAskT: 0,
    pendingGate: null,
    warn: 0,
    yieldT: 0,
    holdBonus: 0,
    blink: Math.random() * 10,
    strobe: Math.random() * 2,
    fade: 0,
    dead: false,
    crashed: false,
    tut: false,
    delayed: false,
    delayed2: false,
    delayed3: false,
  };
}

/**
 * Выбрать случайный тип ВС.
 */
export function pickAircraftType(): AircraftTypeKey {
  const r = Math.random();
  if (r < 0.5) return "jet";
  if (r < 0.8) return "regional";
  return "cargo";
}

/**
 * Поставить борт в зону ожидания на поле.
 */
export function spawnPlane(
  g: GameState,
  hooks: GameHooks,
  typeKey?: AircraftTypeKey | null,
  priority = false,
  tut = false,
): Plane {
  const key = typeKey ?? pickAircraftType();
  const p = createPlane(g, key, priority);
  p.tut = !!tut;
  const slot = g.planes.filter((q) => q.status === "approach").length;
  p.orbitR = 0;
  p.orbitIdx = slot;
  p.orbit = rnd(0, Math.PI * 2);
  const o = orbitPos(p, slot + 1);
  p.x = o.x;
  p.y = o.y;
  p.ang = p.orbit + (p.orbitDir > 0 ? Math.PI / 2 : -Math.PI / 2);
  p.alt = 1;
  p.fade = 0;
  p.joining = false;
  g.planes.push(p);
  if (priority) {
    hooks.toast(
      `ПРИОРИТЕТ ${p.cs} (${p.FK.label}) — терпение тает быстрее, очков больше`,
      "wx",
    );
  }
  return p;
}
